import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUndoAlt, FaUpload } from 'react-icons/fa';
import U2000BasicDialog from '../../../../u2000Core/components/dialog/U2000BasicDialog';
import { useToast } from '../../../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../../../U3141';
import { U3141DocumentFileUpload } from '../u3141ChildLotChain/U3141DocumentFileUpload';

/** Taille max PDF chaîne (aligné middleware backend 50 Mo). */
const MAX_CHAIN_PDF_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const cancelButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-xs transition-all duration-200
  hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
  active:bg-gray-100 active:scale-95
  disabled:cursor-not-allowed disabled:opacity-60`;
const primaryButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg shadow-xs transition-all duration-200
  hover:bg-green-700 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:ring-offset-2
  active:bg-green-800 active:scale-95
  disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-200 disabled:opacity-60 disabled:shadow-none`;

export interface U3141ReconstituteChainPdfDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (file: File) => Promise<void>;
  submitting?: boolean;
}

export const U3141ReconstituteChainPdfDialog: React.FC<U3141ReconstituteChainPdfDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  submitting = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [formResetKey, setFormResetKey] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setFormResetKey((k) => k + 1);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!file || submitting) return;
    if (file.size > MAX_CHAIN_PDF_FILE_SIZE_BYTES) {
      toast.error(t('msgDocumentFileTooLarge', { ns: NS }));
      return;
    }
    await onSubmit(file);
  };

  if (!isOpen) return null;

  return (
    <U2000BasicDialog
      isOpen={isOpen}
      onClose={() => onClose()}
      title={t('dlgImportChainFromPdfTitle', { ns: NS })}
      width="medium"
      height="max-h-[90vh]"
    >
      <div className="flex flex-col h-full focus:outline-hidden">
        <section className="flex-1 px-6 py-4 overflow-visible min-h-0">
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-700">{t('dlgImportChainFromPdfMessage', { ns: NS })}</p>
            <U3141DocumentFileUpload
              key={formResetKey}
              label={t('lblChainPdfFile', { ns: NS })}
              value={file}
              onChange={setFile}
              accept=".pdf,application/pdf"
              disabled={submitting}
            />
          </div>
        </section>
        <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <button type="button" className={cancelButtonClass} onClick={() => onClose()} disabled={submitting}>
            <FaUndoAlt className="h-4 w-4" />
            {t('dlgLblCancel', { ns: NS })}
          </button>
          <button
            type="button"
            className={primaryButtonClass}
            onClick={() => void handleSubmit()}
            disabled={!file || submitting}
          >
            <FaUpload className="h-4 w-4" />
            {t('btnImportChainFromPdfSubmit', { ns: NS })}
          </button>
        </footer>
      </div>
    </U2000BasicDialog>
  );
};
